import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Check, Loader2, KeyRound, CreditCard, QrCode, ChevronLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { PromoBox, FreeRedeem } from "./PromoBox";
import QRCode from "qrcode";
import { PAYPAL_ME, paypalMeUrl, paypalEnabled, buyPro, buyProPaypal, verifyLicense, getLicense, post, get, deviceId, webOrigin } from "../lib/pro";

export const PRICE = "S$9.90";
const PERKS = ["AI writing assistant: detect issues, suggest, rephrase, reformat", "One payment, yours for life — no subscription", "Use on up to 3 devices with your licence key", "Supports future development of PaperLite"];
const ORDER_KEY = "lw.paynowOrder";

export const usePro = () => {
  const [lic, setLic] = useState(getLicense());
  useEffect(() => {
    const h = () => setLic(getLicense());
    window.addEventListener("lw-pro", h);
    return () => window.removeEventListener("lw-pro", h);
  }, []);
  return lic;
};

const PayNowManual = ({ onBack, onDone, method, promo }) => {
  const pp = method === "paypal";
  const price = promo ? promo.price : 9.9;
  const priceTxt = `S$${price.toFixed(2)}`;
  const [qr, setQr] = useState(pp ? "" : "/paynow-qr.jpeg");
  useEffect(() => { if (pp) QRCode.toDataURL(paypalMeUrl(price), { margin: 1, width: 288, color: { dark: "#003087" } }).then(setQr); }, [pp, price]);
  const [email, setEmail] = useState("");
  const [ref, setRef] = useState("");
  const [order, setOrder] = useState(localStorage.getItem(ORDER_KEY) || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const o = await post("/api/orders/paynow", { email, reference: ref, device_id: deviceId(), origin_url: webOrigin(), method, promo_code: promo?.code || "" });
      localStorage.setItem(ORDER_KEY, o.order_id);
      localStorage.setItem(`${ORDER_KEY}.method`, method);
      setOrder(o.order_id);
      toast.success("Order sent. Your licence arrives once the payment is confirmed.");
    } catch (x) { toast.error(x.message); }
    setBusy(false);
  };
  const check = async () => {
    setBusy(true);
    try {
      const o = await get(`/api/orders/paynow/${order}`);
      if (o.status === "approved" && (await verifyLicense(o.license_key))) { localStorage.removeItem(ORDER_KEY); onDone(); }
      else if (o.status === "approved") { localStorage.removeItem(ORDER_KEY); setOrder(""); toast.error("Your payment is confirmed, but this licence has reached its device limit"); }
      else if (o.status === "rejected") { localStorage.removeItem(ORDER_KEY); setOrder(""); toast.error("The payment could not be matched. Please check the reference and try again."); }
      else toast("Still waiting for the payment to be confirmed");
    } catch (x) { toast.error(x.message); }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid={pp ? "paypal-manual" : "paynow-manual"}>
      <button type="button" onClick={onBack} data-testid="paynow-back-btn" className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground"><ChevronLeft className="h-3.5 w-3.5" />Back</button>
      <div className="flex gap-4 items-start">
        {qr && <img src={qr} alt={pp ? "PayPal QR code" : "PayNow QR code"} className="h-36 w-36 rounded-md border bg-white shrink-0" data-testid={pp ? "paypal-qr-img" : "paynow-qr-img"} />}
        <ol className="text-xs space-y-1.5 list-decimal pl-4">
          {pp ? <li>Scan with your phone camera or PayPal app, or <a href={paypalMeUrl(price)} target="_blank" rel="noopener noreferrer" className="text-primary underline" data-testid="paypal-me-link">open PayPal</a>.</li>
            : <li>Open your banking app (DBS, POSB, OCBC, UOB…) and scan the QR code.</li>}
          <li>Pay <b>{priceTxt}</b>.</li>
          <li>Enter your email and the {pp ? "PayPal transaction ID" : "transaction reference"} below.</li>
        </ol>
      </div>
      {order ? (
        <div className="rounded-lg border bg-muted/50 p-3 space-y-2 text-sm" data-testid="paynow-order-pending">
          <div>Order <span className="font-mono">{order}</span> is waiting for confirmation.</div>
          <Button size="sm" variant="outline" onClick={check} disabled={busy} data-testid="paynow-check-btn">{busy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Check status</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-2">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" data-testid="paynow-email-input" />
          <Input required minLength={2} value={ref} onChange={(e) => setRef(e.target.value)} placeholder={pp ? "PayPal transaction ID" : "PayNow transaction reference"} data-testid="paynow-ref-input" />
          <Button type="submit" className="w-full" disabled={busy} data-testid="paynow-submit-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}I have paid</Button>
        </form>
      )}
    </div>
  );
};

export const ProDialog = ({ open, onClose }) => {
  const lic = usePro();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState("");
  const [manual, setManual] = useState(false);
  const [pp, setPp] = useState(false);
  const [promo, setPromo] = useState(null);
  const price = promo ? `S$${promo.price.toFixed(2)}` : PRICE;
  useEffect(() => { paypalEnabled().then(setPp); }, []);
  useEffect(() => { if (open) setManual(localStorage.getItem(ORDER_KEY) ? localStorage.getItem(`${ORDER_KEY}.method`) || "paynow" : false); }, [open]);
  const buy = async (fn = buyPro, tag = "buy") => {
    setBusy(tag);
    try { await fn(promo?.code || ""); } catch (x) { toast.error(x.message); setBusy(""); }
  };
  const restore = async (e) => {
    e.preventDefault();
    setBusy("key");
    try {
      if (await verifyLicense(key)) onClose();
      else toast.error("That licence key is not valid, or it has reached its device limit");
    } catch (x) { toast.error(x.message); }
    setBusy("");
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-2rem)] rounded-lg sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="pro-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Crown className="h-5 w-5 text-amber-500" />PaperLite Pro</DialogTitle>
          <DialogDescription>One-time purchase · {promo ? <><s className="opacity-60">{PRICE}</s> <b className="text-foreground" data-testid="pro-price">{price}</b></> : <span data-testid="pro-price">{PRICE}</span>}</DialogDescription>
        </DialogHeader>
        {lic ? (
          <div className="rounded-lg border bg-emerald-500/10 p-4 text-sm" data-testid="pro-active">
            <div className="font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5"><Check className="h-4 w-4" />Pro is active on this device</div>
            <div className="mt-1 text-muted-foreground">Licence key: <span className="font-mono text-foreground" data-testid="pro-license-key">{lic}</span></div>
          </div>
        ) : manual ? <PayNowManual key={manual} method={manual} promo={promo} onBack={() => setManual(false)} onDone={onClose} /> : (
          <>
            <ul className="space-y-2 text-sm">{PERKS.map((p) => <li key={p} className="flex gap-2"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{p}</li>)}</ul>
            <PromoBox promo={promo} setPromo={setPromo} />
            {promo && promo.price === 0 ? <FreeRedeem promo={promo} onDone={onClose} /> : <>
            <Button className="w-full" onClick={() => buy()} disabled={!!busy} data-testid="pro-buy-btn">{busy === "buy" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}Buy {price} · card or PayNow (instant)</Button>
            {pp && <Button variant="outline" className="w-full bg-[#FFC439] hover:bg-[#F2BA36] text-[#003087] border-[#FFC439] font-semibold" onClick={() => buy(buyProPaypal, "paypal")} disabled={!!busy} data-testid="pro-paypal-btn">{busy === "paypal" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Pay with PayPal</Button>}
            <Button variant="outline" className="w-full" onClick={() => setManual("paynow")} data-testid="pro-paynow-manual-btn"><QrCode className="h-4 w-4 mr-2" />Pay with our PayNow QR</Button>
            {PAYPAL_ME && <Button variant="outline" className="w-full text-[#003087]" onClick={() => setManual("paypal")} data-testid="pro-paypal-qr-btn"><QrCode className="h-4 w-4 mr-2" />Pay with PayPal QR</Button>}
            </>}
            <form onSubmit={restore} className="border-t pt-4 space-y-2">
              <label className="text-xs font-medium flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" />Have a licence key? Enter it here</label>
              <div className="flex gap-2">
                <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="PL-PRO-XXXX-XXXX-XXXX-XXXX" className="font-mono text-xs" data-testid="pro-license-input" />
                <Button type="submit" variant="outline" disabled={key.length < 6 || !!busy} data-testid="pro-license-submit">{busy === "key" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activate"}</Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
